import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type GroupsEventsContent = {
  id: string;
  hero_image_url: string | null;
  hero_kicker: string | null;
  hero_title: string | null;
  hero_subtitle: string | null;
  hero_cta_label: string | null;
  intro_body: string | null;
};

export type GroupsEventsCard = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  best_for: string | null;
  cta_label: string | null;
  image_url: string | null;
  position: number;
};

export type GroupsEventsFaq = {
  id: string;
  question: string;
  answer: string;
  position: number;
};

export type GroupsEventsGalleryImage = {
  id: string;
  image_url: string;
  alt: string | null;
  position: number;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const CONTENT_COLS =
  "id,hero_image_url,hero_kicker,hero_title,hero_subtitle,hero_cta_label,intro_body";
const CARD_COLS =
  "id,slug,title,description,best_for,cta_label,image_url,position";
const FAQ_COLS = "id,question,answer,position";
const GALLERY_COLS = "id,image_url,alt,position";

export const getGroupsEventsPageData = createServerFn({ method: "GET" })
  .handler(async (): Promise<{
    content: GroupsEventsContent | null;
    cards: GroupsEventsCard[];
    faqs: GroupsEventsFaq[];
    gallery: GroupsEventsGalleryImage[];
  }> => {
    const sb = publicClient();
    const [c, cards, faqs, gallery] = await Promise.all([
      sb
        .from("groups_events_content")
        .select(CONTENT_COLS)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
      sb
        .from("groups_events_cards")
        .select(CARD_COLS)
        .order("position", { ascending: true }),
      sb
        .from("groups_events_faqs")
        .select(FAQ_COLS)
        .order("position", { ascending: true }),
      sb
        .from("groups_events_gallery")
        .select(GALLERY_COLS)
        .order("position", { ascending: true }),
    ]);
    if (c.error) throw new Error(c.error.message);
    if (cards.error) throw new Error(cards.error.message);
    if (faqs.error) throw new Error(faqs.error.message);
    if (gallery.error) throw new Error(gallery.error.message);
    return {
      content: (c.data ?? null) as GroupsEventsContent | null,
      cards: (cards.data ?? []) as GroupsEventsCard[],
      faqs: (faqs.data ?? []) as GroupsEventsFaq[],
      gallery: (gallery.data ?? []) as GroupsEventsGalleryImage[],
    };
  });

// --- Admin ---

const contentSchema = z.object({
  id: z.string().uuid().optional(),
  hero_image_url: z.string().nullable(),
  hero_kicker: z.string().nullable(),
  hero_title: z.string().nullable(),
  hero_subtitle: z.string().nullable(),
  hero_cta_label: z.string().nullable(),
  intro_body: z.string().nullable(),
});

export const upsertGroupsEventsContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => contentSchema.parse(data))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const payload = {
      hero_image_url: data.hero_image_url,
      hero_kicker: data.hero_kicker,
      hero_title: data.hero_title,
      hero_subtitle: data.hero_subtitle,
      hero_cta_label: data.hero_cta_label,
      intro_body: data.intro_body,
    };
    if (data.id) {
      const { error } = await sb
        .from("groups_events_content")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await sb
      .from("groups_events_content")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

const cardSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, and dashes only"),
  title: z.string().min(1).max(200),
  description: z.string().nullable(),
  best_for: z.string().nullable(),
  cta_label: z.string().nullable(),
  image_url: z.string().nullable(),
});

export const upsertGroupsEventsCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => cardSchema.parse(data))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (data.id) {
      const { error } = await sb
        .from("groups_events_cards")
        .update({
          slug: data.slug,
          title: data.title,
          description: data.description,
          best_for: data.best_for,
          cta_label: data.cta_label,
          image_url: data.image_url,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: existing } = await sb
      .from("groups_events_cards")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { data: slugRows } = await sb
      .from("groups_events_cards")
      .select("slug");
    const taken = new Set((slugRows ?? []).map((r) => r.slug as string));
    let uniqueSlug = data.slug;
    let n = 2;
    while (taken.has(uniqueSlug)) uniqueSlug = `${data.slug}-${n++}`;
    const { data: row, error } = await sb
      .from("groups_events_cards")
      .insert({
        slug: uniqueSlug,
        title: data.title,
        description: data.description,
        best_for: data.best_for,
        cta_label: data.cta_label,
        image_url: data.image_url,
        position: nextPos,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteGroupsEventsCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("groups_events_cards")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderGroupsEventsCards = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ ordered_ids: z.array(z.string().uuid()) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await context.supabase
        .from("groups_events_cards")
        .update({ position: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

const faqSchema = z.object({
  id: z.string().uuid().optional(),
  question: z.string().min(1).max(500),
  answer: z.string().min(1).max(5000),
});

export const upsertGroupsEventsFaq = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => faqSchema.parse(d))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (data.id) {
      const { error } = await sb
        .from("groups_events_faqs")
        .update({ question: data.question, answer: data.answer })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: existing } = await sb
      .from("groups_events_faqs")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { data: row, error } = await sb
      .from("groups_events_faqs")
      .insert({ question: data.question, answer: data.answer, position: nextPos })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteGroupsEventsFaq = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("groups_events_faqs")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderGroupsEventsFaqs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ ordered_ids: z.array(z.string().uuid()) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await context.supabase
        .from("groups_events_faqs")
        .update({ position: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

const gallerySchema = z.object({
  id: z.string().uuid().optional(),
  image_url: z.string().url(),
  alt: z.string().nullable(),
});

export const upsertGroupsEventsGallery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => gallerySchema.parse(d))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (data.id) {
      const { error } = await sb
        .from("groups_events_gallery")
        .update({ image_url: data.image_url, alt: data.alt })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: existing } = await sb
      .from("groups_events_gallery")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { data: row, error } = await sb
      .from("groups_events_gallery")
      .insert({ image_url: data.image_url, alt: data.alt, position: nextPos })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteGroupsEventsGalleryImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("groups_events_gallery")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderGroupsEventsGallery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ ordered_ids: z.array(z.string().uuid()) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await context.supabase
        .from("groups_events_gallery")
        .update({ position: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });
